import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';
const API_BASE_URL = 'https://vmi1977988.contaboserver.net/api6';
//const API_BASE_URL = 'http://localhost:8084/api6';

function App() {
  const [currentView, setCurrentView] = useState('home'); // home, candidateForm, quiz, results, adminLogin, adminDashboard
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [quizResult, setQuizResult] = useState(null);
  const [loading, setLoading] = useState(true);

  // Track completed quizzes (one attempt only)
  const [completedQuizzes, setCompletedQuizzes] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('completedQuizzes') || '[]');
    } catch { return []; }
  });

  // Candidat state
  const [candidate, setCandidate] = useState(() => {
    try {
      const saved = localStorage.getItem('candidate');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [candidateForm, setCandidateForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    level: '',
    specialization: '',
    city: ''
  });

  // Admin state
  const [admin, setAdmin] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [dashboardData, setDashboardData] = useState(null);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);

  const location = useLocation();
  const navigate = useNavigate();

  // Check URL path on load to support direct /admin access
  useEffect(() => {
    if (location.pathname === '/admin') {
      if (admin) {
        setCurrentView('adminDashboard');
      } else {
        setCurrentView('adminLogin');
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchCategories();
    if (candidate) {
      fetchCandidateSubmissions(candidate.id);
    }
  }, []);

  const fetchCandidateSubmissions = async (candidateId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/quiz/submissions/candidate/${candidateId}`);
      const submissions = response.data;
      const submissionKeys = submissions.map(s => `${candidateId}_${s.category.id}`);
      setCompletedQuizzes(submissionKeys);
      localStorage.setItem('completedQuizzes', JSON.stringify(submissionKeys));
    } catch (error) {
      console.error('Erreur lors du chargement des soumissions:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/quiz/categories`);
      setCategories(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      setLoading(false);
    }
  };

  // === CANDIDATE FUNCTIONS ===

  const handleCandidateFormChange = (e) => {
    setCandidateForm({
      ...candidateForm,
      [e.target.name]: e.target.value
    });
  };

  const handleCandidateSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_BASE_URL}/candidates/register`, candidateForm);
      const newCandidate = response.data;
      setCandidate(newCandidate);
      localStorage.setItem('candidate', JSON.stringify(newCandidate));

      // Sync their submissions immediately
      await fetchCandidateSubmissions(newCandidate.id);

      setCurrentView('home');
      alert('Bienvenue ! Vous pouvez maintenant passer un quiz.');
    } catch (error) {
      alert(error.response?.data?.error || 'Erreur lors de l\'inscription');
    }
  };

  const startQuiz = (category) => {
    if (!candidate) {
      if (window.confirm('Vous devez vous inscrire avant de passer un quiz. Souhaitez-vous vous inscrire maintenant ?')) {
        setCurrentView('candidateForm');
      }
      return;
    }

    // Check if candidate already took ANY quiz
    if (completedQuizzes.length > 0) {
      alert('Désolé, vous avez déjà utilisé votre seule et unique tentative autorisée.');
      return;
    }

    // Deduplicate options in questions
    const cleanCategory = {
      ...category,
      questions: category.questions.map(q => {
        const seen = new Set();
        const uniqueOptions = q.options.filter(opt => {
          if (seen.has(opt)) return false;
          seen.add(opt);
          return true;
        });
        return { ...q, options: uniqueOptions };
      })
    };

    setSelectedCategory(category);
    setCurrentQuiz(cleanCategory);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setSelectedOption(null);
    setQuizResult(null);
    setCurrentView('quiz');
  };

  const handleOptionSelect = (optionIndex) => {
    setSelectedOption(optionIndex);
  };

  const handleNextQuestion = () => {
    if (selectedOption === null) return;

    const newAnswers = [...answers, String.fromCharCode(65 + selectedOption)];
    setAnswers(newAnswers);
    setSelectedOption(null);

    if (currentQuestionIndex < currentQuiz.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      submitQuiz(newAnswers);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      const newAnswers = [...answers];
      const previousAnswer = newAnswers.pop();
      setAnswers(newAnswers);

      if (previousAnswer) {
        setSelectedOption(previousAnswer.charCodeAt(0) - 65);
      } else {
        setSelectedOption(null);
      }
    }
  };

  const submitQuiz = async (finalAnswers) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/quiz/submit`, {
        categoryId: selectedCategory.id,
        answers: finalAnswers,
        candidateId: candidate.id
      });
      setQuizResult(response.data);

      // Mark this quiz as completed (one-time only)
      const quizKey = `${candidate.id}_${selectedCategory.id}`;
      const updated = [...completedQuizzes, quizKey];
      setCompletedQuizzes(updated);
      localStorage.setItem('completedQuizzes', JSON.stringify(updated));

      setCurrentView('results');
    } catch (error) {
      console.error('Erreur lors de la soumission du quiz:', error);
      const errorMsg = error.response?.data?.error || 'Une erreur est survenue lors de la soumission.';
      alert(`Erreur: ${errorMsg}`);
    }
  };

  const restartQuiz = () => {
    setSelectedCategory(null);
    setCurrentQuiz(null);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setSelectedOption(null);
    setQuizResult(null);
    setCurrentView('home');
  };

  const handleCandidateLogout = () => {
    if (window.confirm('Voulez-vous vraiment changer de candidat ?')) {
      setCandidate(null);
      setCompletedQuizzes([]);
      localStorage.removeItem('candidate');
      localStorage.removeItem('completedQuizzes');
      setCurrentView('home');
      setCandidateForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        level: '',
        specialization: '',
        city: ''
      });
    }
  };

  // === ADMIN FUNCTIONS ===

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/login`, loginForm);
      if (response.data.success) {
        setAdmin(response.data.admin);
        setCurrentView('adminDashboard');
        loadDashboardData();
      }
    } catch (error) {
      alert('Identifiants invalides');
    }
  };

  const loadDashboardData = async () => {
    try {
      const [dashResponse, submissionsResponse, candidatesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/dashboard`),
        axios.get(`${API_BASE_URL}/admin/submissions`),
        axios.get(`${API_BASE_URL}/candidates`)
      ]);

      setDashboardData(dashResponse.data);
      setAllSubmissions(submissionsResponse.data);
      setAllCandidates(candidatesResponse.data);
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    }
  };

  const handleAdminLogout = () => {
    setAdmin(null);
    setCurrentView('home');
    setDashboardData(null);
    navigate('/');
  };

  const deleteSubmission = async (id) => {
    if (window.confirm('Confirmer la suppression ?')) {
      try {
        await axios.delete(`${API_BASE_URL}/admin/submissions/${id}`);
        loadDashboardData();
        alert('Soumission supprimée');
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const deleteCandidate = async (id) => {
    if (window.confirm('Confirmer la suppression du candidat ?')) {
      try {
        await axios.delete(`${API_BASE_URL}/candidates/${id}`);
        loadDashboardData();
        alert('Candidat supprimé');
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  // === RENDER VIEWS ===

  if (loading) {
    return (
      <div className="app-container">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  // Results View
  if (currentView === 'results' && quizResult) {
    return (
      <div className="app-container">
        <Header candidate={candidate} onLogout={handleCandidateLogout} onAdminClick={() => { setCurrentView('adminLogin'); navigate('/admin'); }} />
        <div className="results-container">
          <div className="results-icon">
            {quizResult.score === 5 ? '🎉' : quizResult.score >= 3 ? '👏' : '💪'}
          </div>
          <h2>Résultats du Quiz</h2>
          <p className="candidate-name">Candidat : {candidate.firstName} {candidate.lastName}</p>
          <div className="score-display">
            {quizResult.score} / {quizResult.totalQuestions}
          </div>

          {quizResult.score >= 3 && (
            <div className="discount-badge" style={{ fontSize: '1.4rem', padding: '15px 30px' }}>
              🎯 Remise de {quizResult.score === 5 ? '2000' : quizResult.score === 4 ? '1500' : '1000'} DH !
            </div>
          )}

          <p className="results-message">{quizResult.message}</p>

          <div className="bonus-message">
            ⭐ Bonus spécial si inscription le jour même !
          </div>

          <div className="results-actions">
            <button className="btn btn-primary" onClick={restartQuiz}>
              Retour à l'accueil
            </button>
            <button className="btn btn-secondary" onClick={() => window.location.href = 'https://supemir.com'}>
              S'inscrire maintenant
            </button>
            <button className="btn btn-logout" onClick={handleCandidateLogout}>
              🔄 Nouveau Candidat
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Quiz View
  if (currentView === 'quiz' && currentQuiz) {
    const question = currentQuiz.questions[currentQuestionIndex];
    const progress = ((currentQuestionIndex + 1) / currentQuiz.questions.length) * 100;

    return (
      <div className="app-container">
        <Header candidate={candidate} onLogout={handleCandidateLogout} onAdminClick={() => { setCurrentView('adminLogin'); navigate('/admin'); }} />
        <div className="quiz-container">
          <div className="quiz-header">
            <h2>{currentQuiz.name}</h2>
            <p>{currentQuiz.description}</p>
            <div className="quiz-progress">
              <div className="quiz-progress-bar" style={{ width: `${progress}%` }}></div>
            </div>
          </div>

          <div className="question-card">
            <div className="question-number">
              Question {currentQuestionIndex + 1} / {currentQuiz.questions.length}
            </div>
            <h3 className="question-text">{question.question}</h3>

            <div className="options-grid">
              {question.options.map((option, index) => (
                <button
                  key={index}
                  className={`option-button ${selectedOption === index ? 'selected' : ''}`}
                  onClick={() => handleOptionSelect(index)}
                >
                  {String.fromCharCode(65 + index)}) {option}
                </button>
              ))}
            </div>
          </div>

          <div className="quiz-navigation">
            <button
              className="btn btn-secondary"
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
            >
              ← Précédent
            </button>
            <button
              className="btn btn-primary"
              onClick={handleNextQuestion}
              disabled={selectedOption === null}
            >
              {currentQuestionIndex === currentQuiz.questions.length - 1 ? 'Terminer' : 'Suivant →'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Candidate Form View
  if (currentView === 'candidateForm') {
    return (
      <div className="app-container">
        <Header candidate={candidate} onLogout={handleCandidateLogout} onAdminClick={() => { setCurrentView('adminLogin'); navigate('/admin'); }} />
        <div className="candidate-form-container">
          <h2>Inscription Candidat</h2>
          <p>Veuillez remplir vos informations pour passer le quiz</p>

          <form onSubmit={handleCandidateSubmit} className="candidate-form">
            <div className="form-row">
              <div className="form-group">
                <label>Prénom *</label>
                <input
                  type="text"
                  name="firstName"
                  value={candidateForm.firstName}
                  onChange={handleCandidateFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  name="lastName"
                  value={candidateForm.lastName}
                  onChange={handleCandidateFormChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={candidateForm.email}
                  onChange={handleCandidateFormChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Téléphone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={candidateForm.phone}
                  onChange={handleCandidateFormChange}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Niveau d'études *</label>
                <select
                  name="level"
                  value={candidateForm.level}
                  onChange={handleCandidateFormChange}
                  required
                >
                  <option value="">-- Choisir --</option>
                  <option value="Bac">Baccalauréat</option>
                  <option value="Bac+1">Bac+1</option>
                  <option value="Bac+2">Bac+2</option>
                  <option value="Bac+3">Bac+3</option>
                  <option value="Bac+4">Bac+4</option>
                  <option value="Bac+5">Bac+5 et plus</option>
                </select>
              </div>
              <div className="form-group">
                <label>Spécialité</label>
                <input
                  type="text"
                  name="specialization"
                  value={candidateForm.specialization}
                  onChange={handleCandidateFormChange}
                  placeholder="Ex: Scientifique, Économique..."
                />
              </div>
            </div>

            <div className="form-group">
              <label>Ville</label>
              <input
                type="text"
                name="city"
                value={candidateForm.city}
                onChange={handleCandidateFormChange}
                placeholder="Ex: Casablanca"
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setCurrentView('home')}>
                Annuler
              </button>
              <button type="submit" className="btn btn-primary">
                S'inscrire et Continuer
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Admin Login View
  if (currentView === 'adminLogin') {
    return (
      <div className="app-container">
        <Header candidate={candidate} onLogout={handleCandidateLogout} onAdminClick={() => { setCurrentView('adminLogin'); navigate('/admin'); }} />
        <div className="admin-login-container">
          <h2>Connexion Administrateur</h2>
          <form onSubmit={handleAdminLogin} className="admin-login-form">
            <div className="form-group">
              <label>Nom d'utilisateur</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Mot de passe</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                required
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setCurrentView('home')}>
                Retour
              </button>
              <button type="submit" className="btn btn-primary">
                Se connecter
              </button>
            </div>
            <p className="admin-hint">Identifiants par défaut: admin / admin123</p>
          </form>
        </div>
      </div>
    );
  }

  // Admin Dashboard View
  if (currentView === 'adminDashboard' && admin) {
    return <AdminDashboard
      admin={admin}
      dashboardData={dashboardData}
      submissions={allSubmissions}
      candidates={allCandidates}
      onLogout={handleAdminLogout}
      onDeleteSubmission={deleteSubmission}
      onDeleteCandidate={deleteCandidate}
      onRefresh={loadDashboardData}
    />;
  }

  // Home View (Category Selection)
  return (
    <div className="app-container">
      <Header candidate={candidate} onLogout={handleCandidateLogout} onAdminClick={() => { setCurrentView('adminLogin'); navigate('/admin'); }} />

      {!candidate && (
        <div className="welcome-banner">
          <p>👋 Bienvenue ! Pour passer un quiz, veuillez d'abord vous inscrire.</p>
          <button className="btn btn-primary" onClick={() => setCurrentView('candidateForm')}>
            S'inscrire
          </button>
        </div>
      )}

      {candidate && (
        <div className="welcome-banner candidate-welcome">
          <p>✅ Bienvenue {candidate.firstName} {candidate.lastName} !</p>
          <p>Choisissez une catégorie de quiz ci-dessous</p>
        </div>
      )}

      <div className="category-selector">
        <h2>Choisissez votre catégorie</h2>
        <div className="categories-grid">
          {categories.map((category) => (
            <div
              key={category.id}
              className="category-card"
              onClick={() => startQuiz(category)}
            >
              <h3>{category.name}</h3>
              <p>{category.description}</p>
              <p style={{ marginTop: '15px', color: '#E6186B', fontWeight: '600' }}>
                5 questions • Jusqu'à 2000 DH de remise
              </p>
              {candidate && completedQuizzes.length > 0 && (
                <p style={{ marginTop: '8px', color: '#999', fontWeight: '600', fontSize: '0.85rem' }}>
                  ✅ Déjà passé
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Header Component
function Header({ candidate, onLogout, onAdminClick }) {
  return (
    <div className="header">
      <div className="header-logo">
        <img src="/logo/logo.jpeg" alt="SUPEMIR" />
        <div>
          <h1>Quiz</h1>
          <p>Testez vos connaissances</p>
        </div>
      </div>
      <div className="header-actions">
        {candidate && (
          <div className="user-info">
            <span className="user-badge">👤 {candidate.firstName} {candidate.lastName}</span>
            <button className="btn-admin-link" onClick={onLogout}>
              🔄 Changer Candidat
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Admin Dashboard Component
function AdminDashboard({ admin, dashboardData, submissions, candidates, onLogout, onDeleteSubmission, onDeleteCandidate, onRefresh }) {
  const [activeTab, setActiveTab] = useState('stats');
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const viewDetails = (submission) => {
    setSelectedSubmission(submission);
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div>
          <h1>📊 Dashboard Administrateur</h1>
          <p>Bienvenue, {admin.fullName}</p>
        </div>
        <button className="btn btn-secondary" onClick={onLogout}>
          Déconnexion
        </button>
      </div>

      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          📈 Statistiques
        </button>
        <button
          className={`tab ${activeTab === 'submissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('submissions')}
        >
          📝 Soumissions ({submissions.length})
        </button>
        <button
          className={`tab ${activeTab === 'candidates' ? 'active' : ''}`}
          onClick={() => setActiveTab('candidates')}
        >
          👥 Candidats ({candidates.length})
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'stats' && dashboardData && (
          <div className="stats-container">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-value">{dashboardData.totalCandidates}</div>
                <div className="stat-label">Candidats</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📝</div>
                <div className="stat-value">{dashboardData.totalSubmissions}</div>
                <div className="stat-label">Quiz passés</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📚</div>
                <div className="stat-value">{dashboardData.totalCategories}</div>
                <div className="stat-label">Catégories</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📊</div>
                <div className="stat-value">
                  {dashboardData.totalSubmissions > 0
                    ? Math.round((dashboardData.totalSubmissions / dashboardData.totalCandidates) * 10) / 10
                    : 0}
                </div>
                <div className="stat-label">Quiz/Candidat</div>
              </div>
            </div>

            {dashboardData.categoryStats && (
              <div className="category-stats">
                <h3>Performance par Catégorie</h3>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Catégorie</th>
                        <th>Nombre de Quiz</th>
                        <th>Score Moyen</th>
                        <th>Visualisation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.categoryStats.map((stat, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: '600' }}>{stat.categoryName}</td>
                          <td>{stat.count}</td>
                          <td>{(stat.averageScore).toFixed(1)} / 5</td>
                          <td style={{ width: '200px' }}>
                            <div className="stat-bar-container">
                              <div className="stat-bar" style={{ width: `${(stat.averageScore / 5) * 100}%` }}></div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'submissions' && (
          <div className="table-container">
            <div className="table-header">
              <h3>Liste des Soumissions</h3>
              <button className="btn btn-primary" onClick={onRefresh}>🔄 Actualiser</button>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Candidat</th>
                  <th>Catégorie</th>
                  <th>Score</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map(submission => (
                  <tr key={submission.id}>
                    <td>{new Date(submission.submissionDate).toLocaleDateString()}</td>
                    <td>{submission.candidateName}</td>
                    <td>{submission.categoryName}</td>
                    <td>
                      <span className="score-badge">
                        {submission.score}/{submission.totalQuestions}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-detail"
                        onClick={() => viewDetails(submission)}
                      >
                        👁️ Voir
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => onDeleteSubmission(submission.id)}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'candidates' && (
          <div className="table-container">
            <div className="table-header">
              <h3>Liste des Candidats</h3>
              <button className="btn btn-primary" onClick={onRefresh}>🔄 Actualiser</button>
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  <th>Niveau</th>
                  <th>Date inscription</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map(candidate => (
                  <tr key={candidate.id}>
                    <td>{candidate.firstName} {candidate.lastName}</td>
                    <td>{candidate.email}</td>
                    <td>{candidate.phone}</td>
                    <td>{candidate.level}</td>
                    <td>{new Date(candidate.registrationDate).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn-delete"
                        onClick={() => onDeleteCandidate(candidate.id)}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedSubmission && (
        <SubmissionDetailModal
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
        />
      )}
    </div>
  );
}

// Submission Detail Modal
function SubmissionDetailModal({ submission, onClose }) {
  const candidateAnswers = submission.answers ? submission.answers.split(',') : [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="btn-close-modal" onClick={onClose}>&times;</button>

        <div className="details-header">
          <h2>Détails du Quiz</h2>
          <p style={{ color: '#666' }}>ID Soumission: #{submission.id}</p>
        </div>

        <div className="details-grid">
          <div>
            <strong>👤 Candidat:</strong> {submission.candidateName}<br />
            <strong>📧 Email:</strong> {submission.candidateEmail}
          </div>
          <div>
            <strong>📚 Catégorie:</strong> {submission.categoryName}<br />
            <strong>📅 Date:</strong> {new Date(submission.submissionDate).toLocaleString()}<br />
            <strong>🎯 Score:</strong> {submission.score} / {submission.totalQuestions} ({Math.round(submission.percentage)}%)
          </div>
        </div>

        <div className="questions-details-list">
          <h3>Réponses détaillées</h3>
          {submission.questions && submission.questions.map((q, idx) => {
            const candidateAnswer = candidateAnswers[idx];
            const isCorrect = candidateAnswer === q.correctAnswer;

            return (
              <div key={idx} className={`question-detail ${isCorrect ? 'correct' : 'incorrect'}`}>
                <p><strong>Q{idx + 1}:</strong> {q.question}</p>
                <div className="answer-row">
                  <span>Votre réponse: <strong className={isCorrect ? 'badge-correct' : 'badge-incorrect'}>{candidateAnswer}</strong></span>
                  {!isCorrect && <span>| Bonne réponse: <strong className="badge-correct">{q.correctAnswer}</strong></span>}
                  <span style={{ marginLeft: 'auto' }}>{isCorrect ? '✅' : '❌'}</span>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '8px' }}>
                  Options: {q.options.map((opt, i) => `${String.fromCharCode(65 + i)}) ${opt}`).join(' | ')}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default App;
